import { createSelector } from '@ngrx/store';

import { FeatureFlagKeys } from '../app-entities/feature-flag/feature-flag-state.model';
import { featureFlagSelectors } from '../app-entities/feature-flag/feature-flag-state.selectors';
import { selectIsFreeTier } from '../app-global/entitlements/entitlements.selectors';
import { LanguageSelectors } from '../app-global/language/language.selectors';
import { APP_ROUTES } from '../app-global/router/app-routes';
import { RouterSelectors } from '../app-global/router/route.selectors';

import { selectVisiblePeopleTabs } from './people-tabs.selectors';

const swapNavSlots = <T extends { key: string; inMainNav: boolean }>(items: T[], a: string, b: string): T[] => {
  const i = items.findIndex(item => item.key === a);
  const j = items.findIndex(item => item.key === b);
  if (i < 0 || j < 0) return items;
  const swapped = [...items];
  swapped[i] = { ...items[j], inMainNav: items[i].inMainNav };
  swapped[j] = { ...items[i], inMainNav: items[j].inMainNav };
  return swapped;
};

/**
 * The bottom navigation bar's items, minus any section the current company cannot use.
 *
 * Three conditional items today: People, because every tab inside it can be hidden and it's the
 * section's sole entry point, so with none visible the item leads nowhere; Directory, which
 * free-tier companies aren't entitled to at all; and Scorecard, still behind its rollout flag.
 */
export const selectEntitledGlobalNavItems = createSelector(
  LanguageSelectors.globalNavLanguage,
  selectVisiblePeopleTabs,
  selectIsFreeTier,
  featureFlagSelectors.selectById(FeatureFlagKeys.mobileScorecard),
  (navItems, visiblePeopleTabs, isFreeTier, scorecardEnabled) =>
    (scorecardEnabled ? swapNavSlots(navItems, APP_ROUTES.Rocks, APP_ROUTES.Scorecard) : navItems).filter(item => {
      switch (item.key) {
        case APP_ROUTES.People:
          return visiblePeopleTabs.length > 0;
        case APP_ROUTES.Directory:
          return !isFreeTier;
        case APP_ROUTES.Scorecard:
          return !!scorecardEnabled;
        default:
          return true;
      }
    })
);

export const selectGlobalNavItems = createSelector(selectEntitledGlobalNavItems, navItems =>
  navItems.filter(item => item.inMainNav)
);

export const selectGlobalMoreItems = createSelector(selectEntitledGlobalNavItems, navItems =>
  navItems.filter(item => !item.inMainNav)
);

/**
 * The nav item whose section the current route sits in, for the bar's selected state.
 *
 * `ion-tab-button` tracked this itself; the bar's plain buttons have to derive it. Matched on the
 * section root as a prefix so a screen deep inside a section keeps its item lit, which also covers
 * People — its screens all live under `/app/people/`, never at the bare root.
 */
export const selectActiveGlobalNavKey = createSelector(
  selectEntitledGlobalNavItems,
  RouterSelectors.selectRoutePath,
  (navItems, path) =>
    navItems.find(item => {
      const root = `/${APP_ROUTES.App}/${item.key}`;
      return path === root || path.startsWith(`${root}/`);
    })?.key
);
